// ============================================================================
// CUSTOMER GROUP SERVICE — the business-house master (ADR-0035).
// Enterprise-shared; entity comes from X-Entity-ID purely to scope reads.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { customerGroupEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  PartyGroupDto, CreatePartyGroupRequest, UpdatePartyGroupRequest, PartyGroupQueryParams,
} from '../types';

class CustomerGroupService {
  async getAll(params?: PartyGroupQueryParams): Promise<PaginatedResponse<PartyGroupDto>> {
    const endpoint = params ? buildEndpointWithQuery(customerGroupEndpoints.list(), params) : customerGroupEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<PartyGroupDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Active groups for dropdowns / report filters. Backend gates this on [Authorize] only. */
  async getLookup(): Promise<PartyGroupDto[]> {
    const response = await get<ApiResponse<PartyGroupDto[]>>(customerGroupEndpoints.lookup());
    return Array.from(response.data.data || []);
  }

  async getById(id: ID): Promise<PartyGroupDto> {
    const response = await get<ApiResponse<PartyGroupDto>>(customerGroupEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreatePartyGroupRequest): Promise<PartyGroupDto> {
    const response = await post<ApiResponse<PartyGroupDto>>(customerGroupEndpoints.create(), data);
    return response.data.data;
  }

  async update(id: ID, data: UpdatePartyGroupRequest): Promise<PartyGroupDto> {
    const response = await put<ApiResponse<PartyGroupDto>>(customerGroupEndpoints.update(id), data);
    return response.data.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(customerGroupEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = customerGroupEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const customerGroupService = new CustomerGroupService();
export default customerGroupService;
